/**
 * The API's reply contracts, checked in the browser without zod.
 *
 * The contracts are written once, in zod, in packages/shared, and the Worker
 * checks every reply against them before sending it. The browser checks them
 * again on arrival (api.ts has why). Until ADR-010 it did that with zod itself,
 * which was 27 KB of the page's 45 KB of script; Clerk's script is 81 KB, and
 * both together would have broken the 120 KB budget in
 * docs/performance-budget.md.
 *
 * So the browser checks the same contracts as JSON Schema, which zod writes
 * out (contracts.generated.ts, regenerated and compared by
 * test/contracts.test.ts, so it cannot fall behind the zod), with the small
 * checker below. It understands exactly the keywords zod emits for these
 * contracts; the generator refuses any other keyword, so a contract this
 * checker cannot judge fails the tests rather than passing unchecked.
 *
 * One rule follows zod rather than JSON Schema: keys a contract does not name
 * are allowed, because zod's parse allows them too. The API may add a field
 * before the browser learns about it; that is not a broken reply.
 */

import type { Contract } from "./api";

export interface JsonSchema {
  readonly type?: string | readonly string[];
  readonly const?: unknown;
  readonly enum?: readonly unknown[];
  readonly format?: string;
  readonly pattern?: string;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly minimum?: number;
  readonly maximum?: number;
  readonly exclusiveMinimum?: number;
  readonly exclusiveMaximum?: number;
  readonly properties?: Readonly<Record<string, JsonSchema>>;
  readonly required?: readonly string[];
  readonly additionalProperties?: JsonSchema | boolean;
  readonly propertyNames?: JsonSchema;
  readonly items?: JsonSchema;
  readonly maxItems?: number;
  readonly anyOf?: readonly JsonSchema[];
  readonly oneOf?: readonly JsonSchema[];
}

/** Every keyword check() reads. The generator refuses any schema using another. */
export const KNOWN_KEYWORDS: readonly string[] = [
  "type", "const", "enum", "format", "pattern", "minLength", "maxLength", "minimum", "maximum",
  "exclusiveMinimum", "exclusiveMaximum", "properties", "required", "additionalProperties",
  "propertyNames", "items", "maxItems", "anyOf", "oneOf",
];

function typeOf(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (typeof value === "number") return Number.isInteger(value) ? "integer" : "number";
  return typeof value;
}

function typeMatches(value: unknown, type: string | readonly string[]): boolean {
  const actual = typeOf(value);
  const wanted = typeof type === "string" ? [type] : type;
  return wanted.some((t) => t === actual || (t === "number" && actual === "integer"));
}

const patterns = new Map<string, RegExp>();
function regex(source: string): RegExp {
  let compiled = patterns.get(source);
  if (!compiled) {
    compiled = new RegExp(source);
    patterns.set(source, compiled);
  }
  return compiled;
}

/** Whether a value satisfies a schema. */
export function check(schema: JsonSchema, value: unknown): boolean {
  if (schema.type !== undefined && !typeMatches(value, schema.type)) return false;
  if ("const" in schema && value !== schema.const) return false;
  if (schema.enum && !schema.enum.includes(value)) return false;

  if (typeof value === "string") {
    if (schema.pattern !== undefined && !regex(schema.pattern).test(value)) return false;
    if (schema.minLength !== undefined && value.length < schema.minLength) return false;
    if (schema.maxLength !== undefined && value.length > schema.maxLength) return false;
  }
  if (typeof value === "number") {
    if (schema.minimum !== undefined && value < schema.minimum) return false;
    if (schema.maximum !== undefined && value > schema.maximum) return false;
    if (schema.exclusiveMinimum !== undefined && value <= schema.exclusiveMinimum) return false;
    if (schema.exclusiveMaximum !== undefined && value >= schema.exclusiveMaximum) return false;
  }
  if (Array.isArray(value)) {
    if (schema.maxItems !== undefined && value.length > schema.maxItems) return false;
    const items = schema.items;
    if (items && !value.every((item) => check(items, item))) return false;
  }
  if (typeOf(value) === "object") {
    const record = value as Record<string, unknown>;
    for (const key of schema.required ?? []) {
      if (!(key in record)) return false;
    }
    const properties = schema.properties ?? {};
    for (const [key, item] of Object.entries(record)) {
      const declared = properties[key];
      if (declared) {
        if (!check(declared, item)) return false;
      } else if (typeof schema.additionalProperties === "object") {
        if (schema.propertyNames && !check(schema.propertyNames, key)) return false;
        if (!check(schema.additionalProperties, item)) return false;
      }
    }
  }
  if (schema.anyOf && !schema.anyOf.some((option) => check(option, value))) return false;
  if (schema.oneOf && !schema.oneOf.some((option) => check(option, value))) return false;
  return true;
}

/** A contract with the shape api.ts expects, so callers cannot tell it from zod. */
export function contract<T>(schema: JsonSchema): Contract<T> {
  return {
    safeParse(value: unknown) {
      return check(schema, value) ? { success: true, data: value as T } : { success: false };
    },
  };
}
